import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getManagedMembers,
  ManagedMember,
} from 'src/api/dataVault/managedMembers';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface State {
  status: Status;
  members: ManagedMember[];
  error: string | null;
  lastFetchedAt: number | null;
}

const STALE_MS = 60_000;

const initialState: State = {
  status: 'idle',
  members: [],
  error: null,
  lastFetchedAt: null,
};

export const fetchManagedMembers = createAsyncThunk(
  'dataVaultManagedMembers/fetch',
  async (
    { forceRefresh = false }: { forceRefresh?: boolean } = {},
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { dataVaultManagedMembers: State })
      .dataVaultManagedMembers;
    if (
      !forceRefresh &&
      state.status === 'succeeded' &&
      state.lastFetchedAt &&
      Date.now() - state.lastFetchedAt < STALE_MS
    ) {
      return null; // cached
    }
    try {
      return await getManagedMembers();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load managed members'
      );
    }
  }
);

const slice = createSlice({
  name: 'dataVaultManagedMembers',
  initialState,
  reducers: {
    invalidate(state) {
      state.status = 'idle';
      state.lastFetchedAt = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchManagedMembers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchManagedMembers.fulfilled, (state, action) => {
        if (action.payload === null) {
          return;
        } // cached
        state.status = 'succeeded';
        state.members = action.payload;
        state.error = null;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchManagedMembers.rejected, (state, action) => {
        state.status = 'failed';
        state.members = [];
        state.error = action.payload as string;
        state.lastFetchedAt = null;
      });
  },
});

export const { invalidate } = slice.actions;

export const selectManagedMembers = (state: {
  dataVaultManagedMembers: State;
}): State => state.dataVaultManagedMembers;

export default slice.reducer;
